/**
 * Text and geometry of employee availability in the planner (#2041). The server sends
 * structured data (check-conflicts `availability`, the range read); the planner words it
 * here and works out which hours to hatch.
 */
import type { I18n } from '@lingui/core';
import { shifts_AvailabilityWarningResponse } from '@/api';
import type { AvailabilityWarning } from '@/types/availability';

interface WindowLike {
  start_time: string;
  end_time: string;
  kind: string;
}

/** "on Tuesdays" / "dienstags" for an ISO weekday. */
export function weekdayAdverb(weekday: number, i18n: I18n): string {
  switch (weekday) {
    case 1:
      return i18n._({ id: 'shifts.availability.weekday.1', message: 'on Mondays' });
    case 2:
      return i18n._({ id: 'shifts.availability.weekday.2', message: 'on Tuesdays' });
    case 3:
      return i18n._({ id: 'shifts.availability.weekday.3', message: 'on Wednesdays' });
    case 4:
      return i18n._({ id: 'shifts.availability.weekday.4', message: 'on Thursdays' });
    case 5:
      return i18n._({ id: 'shifts.availability.weekday.5', message: 'on Fridays' });
    case 6:
      return i18n._({ id: 'shifts.availability.weekday.6', message: 'on Saturdays' });
    default:
      return i18n._({ id: 'shifts.availability.weekday.7', message: 'on Sundays' });
  }
}

function formatWindows(windows: WindowLike[]): string {
  return windows.map((w) => `${w.start_time}–${w.end_time}`).join(', ');
}

/** "21.10." from "2026-10-21". */
function shortDate(isoDate: string): string {
  const [, month, day] = isoDate.split('-');
  return `${day}.${month}.`;
}

/**
 * The one line the dialogs and the drop dialog show, e.g. "Max ist dienstags nur
 * 08:00–13:00 verfügbar" or "Max hat den 21.10. blockiert: Zahnarzt".
 */
export function availabilityLine(warning: AvailabilityWarning, name: string, i18n: I18n): string {
  if (warning.kind === shifts_AvailabilityWarningResponse.kind.BLOCKED_DAY) {
    const values = { name, date: shortDate(warning.shift_date), note: warning.note ?? '' };
    return warning.note
      ? i18n._({ id: 'shifts.availability.blockedWithNote', message: '{name} blocked {date}: {note}', values })
      : i18n._({ id: 'shifts.availability.blocked', message: '{name} blocked {date}', values });
  }
  const available = warning.windows.filter((w) => w.kind === 'available');
  const weekday = weekdayAdverb(warning.weekday, i18n);
  if (available.length > 0) {
    return i18n._({
      id: 'shifts.availability.onlyAvailable',
      message: '{name} is only available {windows} {weekday}',
      values: { name, windows: formatWindows(available), weekday },
    });
  }
  return i18n._({
    id: 'shifts.availability.notAvailable',
    message: '{name} is not available {windows} {weekday}',
    values: { name, windows: formatWindows(warning.windows), weekday },
  });
}

/** A span of minutes after midnight, [startMinutes, endMinutes). */
export interface MinuteSegment {
  startMinutes: number;
  endMinutes: number;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/**
 * The unavailable spans of one day on the planner axis, clipped to it. With available
 * windows, everything outside their union is unavailable; unavailable windows are
 * unavailable themselves. A day without windows has none.
 */
export function unavailableSegments(
  windows: WindowLike[],
  axis: { startHour: number; endHour: number }
): MinuteSegment[] {
  const axisStart = axis.startHour * 60;
  const axisEnd = axis.endHour * 60;
  const blocked: MinuteSegment[] = [];
  const available = windows
    .filter((w) => w.kind === 'available')
    .map((w) => ({ startMinutes: toMinutes(w.start_time), endMinutes: toMinutes(w.end_time) }))
    .sort((a, b) => a.startMinutes - b.startMinutes);
  if (available.length > 0) {
    let cursor = axisStart;
    for (const w of available) {
      if (w.startMinutes > cursor) blocked.push({ startMinutes: cursor, endMinutes: Math.min(w.startMinutes, axisEnd) });
      cursor = Math.max(cursor, w.endMinutes);
    }
    if (cursor < axisEnd) blocked.push({ startMinutes: cursor, endMinutes: axisEnd });
  }
  for (const w of windows) {
    if (w.kind !== 'unavailable') continue;
    blocked.push({ startMinutes: toMinutes(w.start_time), endMinutes: toMinutes(w.end_time) });
  }
  return blocked
    .map((s) => ({ startMinutes: Math.max(s.startMinutes, axisStart), endMinutes: Math.min(s.endMinutes, axisEnd) }))
    .filter((s) => s.endMinutes > s.startMinutes)
    .sort((a, b) => a.startMinutes - b.startMinutes);
}
